/**
 * SignatureAdoption — the v2 ceremony's "adopt your signature" step (CB-5.5, FR-50 / UX-DR17).
 *
 * Wraps the SHARED SignaturePad (Type / Draw / Upload — built in CB-3.6, reused here unchanged) in
 * the trust-forward ceremony register and commits the chosen signature ("Adopt") into ceremony
 * session state. The adopted signature is held by the shell and applied to the signer's own
 * signature/initial fields in CB-5.6 (the field overlay + token-scoped fill RPC live there) — this
 * step produces the persistence-agnostic adopted-signature artifact, not the field writes.
 *
 * Mobile-first (NFR-3): single column, ≥44px primary control. The Type tab is the keyboard
 * alternative to Draw (a11y floor). No viewport changes (CB-5.4 owns the zoom-never-disabled meta).
 */

import { useState } from 'react';
import { PenLine } from 'lucide-react';
import { SignaturePad } from './SignaturePad';
import { type SignatureValue, hasSignature } from '@/components/settings/contracts/storedSignature';

interface SignatureAdoptionProps {
  /** Default for the Type tab (the signer's resolved name — never typed-in for identity). */
  signerName: string | null;
  accent: string;
  ink: string;
  /** Commit the adopted signature into ceremony session state; the shell advances to field review. */
  onAdopt: (value: SignatureValue) => void;
}

export default function SignatureAdoption({ signerName, accent, ink, onAdopt }: SignatureAdoptionProps) {
  const [value, setValue] = useState<SignatureValue>({});
  const ready = hasSignature(value);

  return (
    <section className="mt-6" aria-labelledby="adopt-heading">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 id="adopt-heading" className="text-lg font-semibold text-slate-900">
          Adopt your signature
        </h2>
        <p className="mt-1 text-base leading-relaxed text-slate-600">
          Type, draw, or upload your signature once. You'll apply it to your fields next.
        </p>

        <div className="mt-4">
          <SignaturePad value={value} onChange={setValue} typedNameDefault={signerName ?? ''} />
        </div>
      </div>

      <button
        type="button"
        onClick={() => ready && onAdopt(value)}
        disabled={!ready}
        className="mt-5 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: accent, color: ink }}
      >
        <PenLine className="h-5 w-5" aria-hidden="true" />
        Adopt signature
      </button>
    </section>
  );
}
